import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Logo.png';

import ApiService, { Credentials } from '../services/apiService';

import {
    containerStyle
} from '../styles/globalStyles';

interface ProductPlan {
    id: string;
    product_id: string;
    name: string;
    description: string;
    priceLabel: string;
    price_id: string;
}

interface CartItem {
    product_id: string;
    quantity: number;
}

interface SubscriptionInfo {
    product_id?: string;
    price_id?: string;
    status?: string;
    current_period_end?: string;
}

type SubscriptionResponse = SubscriptionInfo[] | { subscriptions?: SubscriptionInfo[] };

interface StripePlan {
    id: string;
    name: string;
    description: string;
    status?: string;
    price?: number;
    price_id: string;
}

type StripePlanResponse = StripePlan[] | { subscriptions?: StripePlan[] };

interface ProductSubproduct {
    id: string;
    name?: string;
    description: string;
    images: string[];
}

interface ProductMediaDetails {
    description: string;
    images: string[];
    subproducts?: ProductSubproduct[];
}

const Home: React.FC = () => {
    const [ isLoggedIn, setIsLoggedIn ] = useState( false );
    const [ cartItems, setCartItems ] = useState<CartItem[]>( [] );
    const [ subscriptions, setSubscriptions ] = useState<SubscriptionInfo[]>( [] );
    const [ isCheckoutLoading, setIsCheckoutLoading ] = useState( false );
    const [ isSubscriptionLoading, setIsSubscriptionLoading ] = useState( false );
    const [ statusMessage, setStatusMessage ] = useState( '' );
    const [ errorMessage, setErrorMessage ] = useState( '' );
    const [ isCartOpen, setIsCartOpen ] = useState( false );
    const [ productPlans, setProductPlans ] = useState< ProductPlan[] >( [] );
    const [ userApiService, setUserApiService ] = useState< ApiService | null >( null );
    const [ productDetailsById, setProductDetailsById ] = useState< Record< string, ProductMediaDetails > >( {} );
    const [ carouselIndexes, setCarouselIndexes ] = useState< Record< string, number > >( {} );

    const credentials = useMemo< Credentials >( () => ( {
        email: process.env.REACT_APP_ADMIN_USERNAME || '',
        password: process.env.REACT_APP_ADMIN_PASSWORD || ''
    } ), [] );

    const apiService = useMemo( () => new ApiService( credentials ), [ credentials ] );
    const navigate = useNavigate();

    useEffect( () => {
        const storedLogin = localStorage.getItem( 'rememberedLogin' );
        setIsLoggedIn( Boolean( storedLogin ) );

        if( ! storedLogin ){
            setUserApiService( null );
            setIsLoggedIn( false );

            return;
        }

        try{
            const parsed = JSON.parse( storedLogin );

            if( parsed?.email && parsed?.password ){
                setUserApiService( new ApiService( { email: parsed.email, password: parsed.password } ) );
                setIsLoggedIn( true );
            }else{
                setUserApiService( null );
                setIsLoggedIn( false );
            }
        }catch( err ){
            setUserApiService( null );
            setIsLoggedIn( false );
        }
    }, [] );

    useEffect( () => {
        const fetchProductPlans = async () => {
            setIsSubscriptionLoading( true );
            setErrorMessage( '' );

            try{
                const data = await apiService.request< StripePlanResponse >( 'GET', '/api/v1/stripe/subscriptions' );
                const planList = Array.isArray( data ) ? data : ( data?.subscriptions || [] );
                const mappedPlans = planList.map( ( plan ) => ( {
                    id: plan.id,
                    product_id: plan.id,
                    name: plan.name,
                    description: plan.description,
                    priceLabel: typeof plan.price === 'number' ? `$${plan.price.toFixed( 2 )} / month` : 'Contact sales',
                    price_id: plan.price_id
                } ) ).filter( ( plan ) => plan.id && plan.price_id );

                setProductPlans( mappedPlans );
            }catch( err ){
                setErrorMessage( err instanceof Error ? err.message : 'Failed to load subscriptions.' );
            }finally{
                setIsSubscriptionLoading( false );
            }
        };

        fetchProductPlans();
    }, [ apiService ] );

    useEffect( () => {
        let isMounted = true;

        const fetchProductDetails = async () => {
            try{
                const response = await fetch( `${process.env.PUBLIC_URL}/product-data/Products.json` );

                if( ! response.ok )
                    return;

                const details = await response.json() as Array< ProductMediaDetails & { id: string } >;
                const nextDetails: Record< string, ProductMediaDetails > = {};

                details.forEach( ( detail ) => {
                    if( detail.id )
                        nextDetails[ detail.id ] = detail;
                } );

                if( ! isMounted )
                    return;

                setProductDetailsById( nextDetails );
            }catch{
                return;
            }
        };

        fetchProductDetails();

        return () => {
            isMounted = false;
        };
    }, [] );

    const updateCarouselIndex = ( key: string, nextIndex: number ) => {
        setCarouselIndexes( ( prev ) => ( { ...prev, [ key ]: nextIndex } ) );
    };

    const carouselImageCounts = useMemo( () => {
        const counts: Record< string, number > = {};

        Object.entries( productDetailsById ).forEach( ( [ productId, details ] ) => {
            if( details.images?.length )
                counts[ `product-${productId}` ] = details.images.length;

            details.subproducts?.forEach( ( subproduct ) => {
                if( subproduct.images?.length )
                    counts[ `subproduct-${productId}-${subproduct.id}` ] = subproduct.images.length;
            } );
        } );

        return counts;
    }, [ productDetailsById ] );

    useEffect( () => {
        if( Object.keys( carouselImageCounts ).length === 0 )
            return;

        const intervalId = window.setInterval( () => {
            setCarouselIndexes( ( prev ) => {
                const next: Record< string, number > = { ...prev };

                Object.entries( carouselImageCounts ).forEach( ( [ key, count ] ) => {
                    if( count <= 1 )
                        return;

                    const currentIndex = next[ key ] ?? 0;
                    next[ key ] = ( currentIndex + 1 ) % count;
                } );

                return next;
            } );
        }, 5000 );

        return () => {
            window.clearInterval( intervalId );
        };
    }, [ carouselImageCounts ] );

    useEffect( () => {
        if( ! isLoggedIn || ! userApiService ){
            return;
        }

        setIsSubscriptionLoading( true );
        setErrorMessage( '' );

        userApiService.request< SubscriptionResponse >( 'GET', '/api/v1/stripe/user-subscriptions' )
            .then( ( data ) => {
                const subscriptionList = Array.isArray( data ) ? data : ( data?.subscriptions || [] );
                setSubscriptions( subscriptionList );
            } )
            .catch( ( err ) => {
                setErrorMessage( err instanceof Error ? err.message : 'Failed to load subscriptions.' );
            } )
            .finally( () => {
                setIsSubscriptionLoading( false );
            } );
    }, [ isLoggedIn, userApiService ] );

    const handleAuthButtonClick = () => {
        if( isLoggedIn ){
            localStorage.removeItem( 'rememberedLogin' );
            setIsLoggedIn( false );
            setSubscriptions( [] );
            setUserApiService( null );
            navigate( '/' );

            return;
        }

        navigate( '/register' );
    };

    const handleLoginClick = () => {
        navigate( '/login' );
    };

    const handleDashboardClick = () => {
        navigate( '/dashboard' );
    };

    const addToCart = ( productId: string ) => {
        setCartItems( ( prev ) => {
            const existing = prev.find( ( item ) => item.product_id === productId );

            if( existing )
                return prev;

            return [ ...prev, { product_id: productId, quantity: 1 } ];
        } );
        setStatusMessage( '' );
        setErrorMessage( '' );
        setIsCartOpen( true );
    };

    const removeFromCart = ( productId: string ) => {
        setCartItems( ( prev ) => prev.filter( ( item ) => item.product_id !== productId ) );
    };

    const hasActiveSubscription = ( subscription?: SubscriptionInfo ) => {
        if( ! subscription?.status )
            return false;

        return subscription.status === 'active' || subscription.status === 'trialing';
    };

    const getSubscriptionForProduct = ( product: ProductPlan ) => {
        return subscriptions.find( ( subscription ) => {
            if( subscription.product_id && subscription.product_id === product.id )
                return true;

            if( subscription.price_id && subscription.price_id === product.price_id )
                return true;

            return false;
        } );
    };

    const getSubscriptionLabel = ( subscription?: SubscriptionInfo ) => {
        if( ! subscription )
            return 'Not purchased';

        const status = subscription.status || 'unknown';

        switch( status ){
            case 'active':
                return 'Active';
            case 'trialing':
                return 'Trialing';
            case 'past_due':
                return 'Past due';
            case 'canceled':
                return 'Canceled';
            case 'incomplete':
                return 'Incomplete';
            default:
                return status;
        }
    };

    const renderCarousel = ( images: string[], key: string, label: string ) => {
        if( images.length === 0 )
            return null;

        const currentIndex = carouselIndexes[ key ] ?? 0;
        const safeIndex = currentIndex % images.length;
        const hasMultiple = images.length > 1;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: 220,
                        borderRadius: 10,
                        border: '1px solid #1d2025',
                        background: '#0b0c0e',
                        overflow: 'hidden'
                    }}
                >
                    <img
                        src={images[ safeIndex ]}
                        alt={label}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block'
                        }}
                    />
                    {hasMultiple && (
                        <>
                            <button
                                type="button"
                                onClick={() => updateCarouselIndex( key, ( safeIndex + images.length - 1 ) % images.length )}
                                style={{
                                    position: 'absolute',
                                    left: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#111214',
                                    border: '1px solid #222',
                                    borderRadius: 14,
                                    color: '#fff',
                                    padding: '14px 20px',
                                    cursor: 'pointer',
                                    fontSize: 22,
                                    fontWeight: 700
                                }}
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                onClick={() => updateCarouselIndex( key, ( safeIndex + 1 ) % images.length )}
                                style={{
                                    position: 'absolute',
                                    right: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#111214',
                                    border: '1px solid #222',
                                    borderRadius: 14,
                                    color: '#fff',
                                    padding: '14px 20px',
                                    cursor: 'pointer',
                                    fontSize: 22,
                                    fontWeight: 700
                                }}
                            >
                                ›
                            </button>
                        </>
                    )}
                </div>
                {hasMultiple && (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        {images.map( ( _image, index ) => (
                            <button
                                key={`${key}-dot-${index}`}
                                type="button"
                                onClick={() => updateCarouselIndex( key, index )}
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: index === safeIndex ? '#22d3ee' : '#1f2937',
                                    cursor: 'pointer'
                                }}
                                aria-label={`Show image ${index + 1}`}
                            />
                        ) )}
                    </div>
                )}
            </div>
        );
    };

    const cartDetails = useMemo( () => {
        return cartItems.map( ( item ) => {
            const product = productPlans.find( ( plan ) => plan.id === item.product_id );

            return {
                ...item,
                product
            };
        } ).filter( ( item ) => item.product );
    }, [ cartItems, productPlans ] );

    const handleCheckout = async () => {
        if( cartItems.length === 0 ){
            setErrorMessage( 'Your cart is empty.' );
            return;
        }

        if( ! isLoggedIn ){
            setErrorMessage( 'Please log in to start a subscription.' );
            return;
        }

        if( ! userApiService ){
            setErrorMessage( 'Please log in to start a subscription.' );
            return;
        }

        const alreadySubscribed = cartDetails
            .filter( ( item ) => item.product && hasActiveSubscription( getSubscriptionForProduct( item.product ) ) )
            .map( ( item ) => item.product?.name )
            .filter( Boolean );

        if( alreadySubscribed.length > 0 ){
            setErrorMessage( `You already have an active subscription for: ${alreadySubscribed.join( ', ' )}.` );
            return;
        }

        const items = cartItems.map( ( item ) => {
            const product = productPlans.find( ( plan ) => plan.id === item.product_id );

            return {
                price_id: product?.price_id || '',
                product_id: product?.id || '',
                quantity: item.quantity
            };
        } );

        if( items.some( ( item ) => ! item.price_id ) ){
            setErrorMessage( 'Missing Stripe price IDs. Configure them in env.' );
            return;
        }

        setIsCheckoutLoading( true );
        setErrorMessage( '' );
        setStatusMessage( '' );

        try{
            const data = await userApiService.request<{ url?: string; session_url?: string; checkout_url?: string }>(
                'POST',
                '/api/v1/stripe/checkout',
                { body: { items } }
            );
            const checkoutUrl = data?.url || data?.session_url || data?.checkout_url;

            if( checkoutUrl ){
                window.location.href = checkoutUrl;
                return;
            }

            setErrorMessage( 'Checkout URL was not returned.' );
        }catch( err ){
            setErrorMessage( 'Failed to start checkout.' );
        }finally{
            setIsCheckoutLoading( false );
        }
    };

    const handleRefreshStatus = () => {
        if( isSubscriptionLoading )
            return;

        setIsSubscriptionLoading( true );
        setErrorMessage( '' );

        if( ! isLoggedIn ){
            setErrorMessage( 'Please log in to view subscription status.' );
            setIsSubscriptionLoading( false );
            return;
        }

        if( ! userApiService ){
            setErrorMessage( 'Please log in to view subscription status.' );
            setIsSubscriptionLoading( false );
            return;
        }

        userApiService.request< SubscriptionResponse >( 'GET', '/api/v1/stripe/user-subscriptions' )
            .then( ( data ) => {
                const subscriptionList = Array.isArray( data ) ? data : ( data?.subscriptions || [] );
                setSubscriptions( subscriptionList );
                setStatusMessage( 'Subscription status refreshed.' );
            } )
            .catch( ( err ) => {
                setErrorMessage( err instanceof Error ? err.message : 'Failed to load subscriptions.' );
            } )
            .finally( () => {
                setIsSubscriptionLoading( false );
            } );
    };

    return(
        <div style={{ ...containerStyle, paddingTop: 80 }}>
            <nav
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 24px',
                    background: '#111214',
                    borderBottom: '1px solid #222',
                    zIndex: 1000
                }}
            >
                <img
                    src={logo}
                    alt="logo"
                    style={{ height: 36, width: 'auto' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    { isLoggedIn ? (
                        <button
                            type="button"
                            onClick={handleDashboardClick}
                            style={{
                                background: 'transparent',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: 16,
                                border: '1px solid #2a2d33',
                                borderRadius: 6,
                                padding: '10px 18px',
                                cursor: 'pointer',
                                letterSpacing: 0.6
                            }}
                        >
                            Dashboard
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleLoginClick}
                            style={{
                                background: 'transparent',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: 16,
                                border: '1px solid #2a2d33',
                                borderRadius: 6,
                                padding: '10px 18px',
                                cursor: 'pointer',
                                letterSpacing: 0.6
                            }}
                        >
                            LogIn
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleAuthButtonClick}
                        style={{
                            background: '#22d3ee',
                            color: '#111214',
                            fontWeight: 700,
                            fontSize: 16,
                            border: 'none',
                            borderRadius: 6,
                            padding: '10px 18px',
                            cursor: 'pointer',
                            letterSpacing: 0.6
                        }}
                    >
                        {isLoggedIn ? 'LogOut' : 'SingUp'}
                    </button>
                </div>
            </nav>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    width: '100%',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    color: '#fff',
                    padding: '120px 24px 80px 24px'
                }}
            >
                <div style={{ width: '100%' }}>
                    <div style={{ marginBottom: 32 }}>
                        <h1 style={{ fontSize: 36, marginBottom: 8 }}>Subscriptions</h1>
                        <p style={{ color: '#b0b3b8', margin: 0 }}>
                            Pick a plan, add it to your cart, and start a monthly subscription.
                        </p>
                    </div>
                    {( errorMessage || statusMessage ) && (
                        <div style={{ marginBottom: 20, color: errorMessage ? '#ef4444' : '#22d3ee' }}>
                            {errorMessage || statusMessage}
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {productPlans.map( ( product ) => {
                            const subscription = getSubscriptionForProduct( product );
                            const subscriptionLabel = getSubscriptionLabel( subscription );
                            const hasSubscription = Boolean( subscription );
                            const isActiveSubscription = hasActiveSubscription( subscription );
                            const productDetails = productDetailsById[ product.id ];
                            const productImages = productDetails?.images ?? [];
                            const productDescription = productDetails?.description || product.description;

                            return (
                                <div
                                    key={product.id}
                                    style={{
                                        border: '1px solid #222',
                                        borderRadius: 12,
                                        padding: 20,
                                        background: '#141619',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 16
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: 22, fontWeight: 700 }}>{product.name}</div>
                                    <div style={{ color: '#b0b3b8', marginTop: 6, whiteSpace: 'pre-line' }}>
                                        {productDescription}
                                    </div>
                                    </div>
                                    {renderCarousel( productImages, `product-${product.id}`, `${product.name} preview` )}
                                    {productDetails?.subproducts && productDetails.subproducts.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div style={{ fontWeight: 600, fontSize: 14, color: '#b0b3b8' }}>
                                                Subproducts
                                            </div>
                                            {productDetails.subproducts.map( ( subproduct ) => (
                                                <div
                                                    key={`${product.id}-${subproduct.id}`}
                                                    style={{
                                                        border: '1px solid #1d2025',
                                                        borderRadius: 10,
                                                        padding: 12,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 10,
                                                        background: '#111214'
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 600 }}>
                                                        {subproduct.name || subproduct.id}
                                                    </div>
                                                    <div style={{ color: '#b0b3b8', fontSize: 13, whiteSpace: 'pre-line' }}>
                                                        {subproduct.description}
                                                    </div>
                                                    {renderCarousel(
                                                        subproduct.images,
                                                        `subproduct-${product.id}-${subproduct.id}`,
                                                        `${subproduct.name || subproduct.id} preview`
                                                    )}
                                                </div>
                                            ) )}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 20, fontWeight: 700 }}>{product.priceLabel}</div>
                                    
                                    {isLoggedIn && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div style={{ color: hasSubscription ? '#22d3ee' : '#b0b3b8', fontWeight: 600 }}>
                                                Status: {isSubscriptionLoading ? 'Loading...' : subscriptionLabel}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => addToCart( product.id )}
                                                disabled={isActiveSubscription}
                                                style={{
                                                    background: isActiveSubscription ? '#1f2937' : '#22d3ee',
                                                    color: isActiveSubscription ? '#b0b3b8' : '#111214',
                                                    fontWeight: 700,
                                                    fontSize: 16,
                                                    border: 'none',
                                                    borderRadius: 6,
                                                    padding: '10px 16px',
                                                    cursor: isActiveSubscription ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isActiveSubscription ? 'Subscription active' : 'Add to cart'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        } )}
                    </div>
                </div>
                <div
                    style={{
                        position: 'fixed',
                        right: 24,
                        top: 96,
                        width: 320,
                        background: '#0f1012',
                        border: '1px solid #222',
                        borderRadius: 12,
                        padding: 16,
                        display: isCartOpen ? 'flex' : 'none',
                        flexDirection: 'column',
                        gap: 12,
                        zIndex: 999
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 700 }}>Cart</div>
                        <button
                            type="button"
                            onClick={() => setIsCartOpen( false )}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#b0b3b8',
                                cursor: 'pointer',
                                fontSize: 18
                            }}
                        >
                            ×
                        </button>
                    </div>
                    {cartDetails.length === 0 && (
                        <div style={{ color: '#b0b3b8' }}>Your cart is empty.</div>
                    )}
                    {cartDetails.map( ( item ) => (
                        <div
                            key={`cart-${item.product_id}`}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                border: '1px solid #1d2025',
                                borderRadius: 8,
                                padding: 12
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 600 }}>{item.product?.name}</div>
                                <button
                                    type="button"
                                    onClick={() => removeFromCart( item.product_id )}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#ef4444',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ) )}
                    <button
                        type="button"
                        onClick={handleCheckout}
                        disabled={isCheckoutLoading}
                        style={{
                            background: isCheckoutLoading ? '#1f2937' : '#22d3ee',
                            color: '#111214',
                            fontWeight: 700,
                            fontSize: 16,
                            border: 'none',
                            borderRadius: 6,
                            padding: '10px 16px',
                            cursor: isCheckoutLoading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isCheckoutLoading ? 'Starting checkout...' : 'Checkout'}
                    </button>
                    <button
                        type="button"
                        onClick={handleRefreshStatus}
                        disabled={isSubscriptionLoading}
                        style={{
                            background: 'transparent',
                            color: '#22d3ee',
                            fontWeight: 600,
                            fontSize: 14,
                            border: '1px solid #22d3ee',
                            borderRadius: 6,
                            padding: '8px 12px',
                            cursor: isSubscriptionLoading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isSubscriptionLoading ? 'Refreshing...' : 'Refresh subscription status'}
                    </button>
                </div>
                <button
                    type="button"
                    onClick={() => setIsCartOpen( ( prev ) => ! prev )}
                    style={{
                        position: 'fixed',
                        right: 24,
                        bottom: 24,
                        background: '#22d3ee',
                        color: '#111214',
                        fontWeight: 700,
                        fontSize: 16,
                        border: 'none',
                        borderRadius: 999,
                        padding: '12px 18px',
                        cursor: 'pointer',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
                    }}
                >
                    Cart ({cartItems.reduce( ( total, item ) => total + item.quantity, 0 )})
                </button>
            </div>
        </div>
    );
};

export default Home; 